import React, { useState, useEffect } from 'react';
import { X, Plus, Copy, Ban, Check } from 'lucide-react';
import { getSsgTvTokens, createSsgTvToken, updateSsgTvToken } from '../../api';

const btn = (active, color = '#3b82f6') => ({
  padding: '7px 14px', borderRadius: 8, border: `1px solid ${active ? color : 'var(--border)'}`,
  background: active ? color : 'transparent', color: active ? '#fff' : 'var(--text-secondary)',
  fontSize: 12.5, fontWeight: active ? 600 : 400, cursor: 'pointer', fontFamily: 'inherit',
});
const inputStyle = {
  padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border)',
  background: 'var(--bg-app)', color: 'var(--text-primary)', fontSize: 13,
  fontFamily: 'inherit', width: '100%', boxSizing: 'border-box',
};

function fmtLastSeen(d) {
  if (!d) return 'Never';
  return new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

export default function SsgTvTokensAdmin({ onClose }) {
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newLabel, setNewLabel] = useState('');
  const [creating, setCreating] = useState(false);
  const [copiedToken, setCopiedToken] = useState(null);

  const refresh = () => {
    setLoading(true);
    getSsgTvTokens('admin').then(rows => setTokens(rows || [])).finally(() => setLoading(false));
  };
  useEffect(() => { refresh(); }, []);

  const handleCreate = async () => {
    if (!newLabel.trim()) return;
    setCreating(true);
    try {
      await createSsgTvToken({ label: newLabel.trim() }, 'admin');
      setNewLabel('');
      refresh();
    } finally { setCreating(false); }
  };

  const handleRevoke = async (token, active) => {
    await updateSsgTvToken(token, { active: !active }, 'admin');
    refresh();
  };

  const handleCopy = (token) => {
    const url = `${window.location.origin}/ssg/tv?token=${token}`;
    navigator.clipboard?.writeText(url).then(() => {
      setCopiedToken(token);
      setTimeout(() => setCopiedToken(null), 1500);
    });
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 200, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 16px', overflowY: 'auto' }}>
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-strong)', borderRadius: 16, width: '100%', maxWidth: 640, boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid var(--border)' }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Dressing-room TV tokens</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}><X size={18} /></button>
        </div>
        <div style={{ padding: 20 }}>
          <p style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 0 }}>
            Each token is a standalone, no-login kiosk URL. Anyone with the link and an active token can view the board.
          </p>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            <input style={inputStyle} placeholder="Label, e.g. &quot;Dressing room TV&quot;" value={newLabel} onChange={e => setNewLabel(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleCreate()} />
            <button style={btn(true)} onClick={handleCreate} disabled={creating || !newLabel.trim()}>
              <Plus size={13} style={{ verticalAlign: -2, marginRight: 4 }} />{creating ? 'Creating…' : 'Create'}
            </button>
          </div>

          {loading ? (
            <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-dimmed)' }}>Loading…</div>
          ) : tokens.length === 0 ? (
            <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-dimmed)' }}>No tokens yet.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {tokens.map(t => (
                <div key={t.token} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 8, background: 'var(--bg-app)', border: '1px solid var(--border-strong)', opacity: t.active ? 1 : 0.5 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700 }}>{t.label || '(unlabeled)'}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                      {t.active ? 'Active' : 'Revoked'} · Last seen: {fmtLastSeen(t.last_seen)}
                    </div>
                  </div>
                  <button style={btn(false)} onClick={() => handleCopy(t.token)} title="Copy kiosk URL">
                    {copiedToken === t.token ? <Check size={13} /> : <Copy size={13} />}
                  </button>
                  <button style={btn(false, t.active ? '#ef4444' : '#10b981')} onClick={() => handleRevoke(t.token, t.active)}>
                    {t.active ? <><Ban size={12} style={{ verticalAlign: -2, marginRight: 4 }} />Revoke</> : 'Reactivate'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
